import { string } from 'yup';

import { pluralize } from '../utils/helpers';

const defaultPasswordOptions = {
  minChars: 8,
  minUpperCaseChars: 1,
  minLowerCaseChars: 1,
  minNumbers: 1,
  minSpecialChars: 1,
  noSpaces: true,
};

export default defaultPasswordOptions;

export const getPasswordMsgs = (options = defaultPasswordOptions) => {
  return {
    minCharsMsg:
      options.minChars > 0
        ? `at least ${options.minChars} ${pluralize(
            options.minChars,
            'character',
          )}`
        : 'at least 1 character',
    minUpperCaseCharsMsg:
      options.minUpperCaseChars > 0
        ? `at least ${options.minUpperCaseChars} ${pluralize(
            options.minUpperCaseChars,
            'uppercase character',
          )}`
        : '',
    minLowerCaseCharsMsg:
      options.minLowerCaseChars > 0
        ? `at least ${options.minLowerCaseChars} ${pluralize(
            options.minLowerCaseChars,
            'lowercase character',
          )}`
        : '',
    minNumbersMsg:
      options.minNumbers > 0
        ? `at least ${options.minNumbers} ${pluralize(
            options.minNumbers,
            'digit',
          )}`
        : '',
    minSpecialCharsMsg:
      options.minSpecialChars > 0
        ? `at least ${options.minSpecialChars}  ${pluralize(
            options.minSpecialChars,
            'special character',
          )}`
        : '',
    noSpacesMsg: options.noSpaces ? `contains no spaces` : '',
  };
};

export const getPasswordRegex = (options = defaultPasswordOptions) => {
  const minCharsRegexBase =
    options?.minChars > 0 ? `(?=.{${options.minChars},})` : '(?=.+)';
  const minUpperCaseCharsRegexBase =
    options?.minUpperCaseChars > 0
      ? `(?=(.*[A-Z)]){${options.minUpperCaseChars},})`
      : '';
  const minLowerCaseCharsRegexBase =
    options?.minLowerCaseChars > 0
      ? `(?=(.*[a-z]){${options.minLowerCaseChars},})`
      : '';
  const minNumbersRegexBase =
    options?.minNumbers > 0 ? `(?=(.*\\d){${options.minNumbers},})` : '';
  const minSpecialCharsRegexBase =
    options?.minSpecialChars > 0
      ? `(?=(.*[!-/:-@[-\`{-~]){${options.minSpecialChars},})`
      : '';
  const noSpacesRegexBase = options?.noSpaces ? `(?!.*\\s)` : '';
  const fullRegexBase = `${minCharsRegexBase}${minUpperCaseCharsRegexBase}${minLowerCaseCharsRegexBase}${minNumbersRegexBase}${minSpecialCharsRegexBase}${noSpacesRegexBase}`;

  return {
    minCharsRegex: RegExp(minCharsRegexBase),
    minUpperCaseCharsRegex: RegExp(minUpperCaseCharsRegexBase),
    minLowerCaseCharsRegex: RegExp(minLowerCaseCharsRegexBase),
    minNumbersRegex: RegExp(minNumbersRegexBase),
    minSpecialCharsRegex: RegExp(minSpecialCharsRegexBase),
    noSpacesRegex: RegExp(noSpacesRegexBase),
    fullRegex: RegExp(`^${fullRegexBase}`),
  };
};

export const getPasswordSchema = (context = defaultPasswordOptions) => {
  const {
    minCharsMsg,
    minUpperCaseCharsMsg,
    minLowerCaseCharsMsg,
    minNumbersMsg,
    minSpecialCharsMsg,
    noSpacesMsg,
  } = getPasswordMsgs(context);
  const {
    minUpperCaseCharsRegex,
    minLowerCaseCharsRegex,
    minNumbersRegex,
    minSpecialCharsRegex,
  } = getPasswordRegex(context);

  return string()
    .when('$minChars', {
      is: () => context?.minChars > 0,
      then: (schema) => schema.min(context?.minChars, { length: minCharsMsg }),
      otherwise: (schema) => schema.min(1, { length: minCharsMsg }),
    })
    .when('$minUpperCaseChars', {
      is: () => context?.minUpperCaseChars > 0,
      then: (schema) =>
        schema.matches(minUpperCaseCharsRegex, {
          message: { uppercase: minUpperCaseCharsMsg },
        }),
      otherwise: (schema) => schema,
    })
    .when('$minLowerCaseChars', {
      is: () => context?.minLowerCaseChars > 0,
      then: (schema) =>
        schema.matches(minLowerCaseCharsRegex, {
          message: { lowercase: minLowerCaseCharsMsg },
        }),
      otherwise: (schema) => schema,
    })
    .when('$minNumbers', {
      is: () => context?.minNumbers > 0,
      then: (schema) =>
        schema.matches(minNumbersRegex, {
          message: { number: minNumbersMsg },
        }),
      otherwise: (schema) => schema,
    })
    .when('$minSpecialChars', {
      is: () => context?.minSpecialChars > 0,
      then: (schema) =>
        schema.matches(minSpecialCharsRegex, {
          message: { special: minSpecialCharsMsg },
        }),
      otherwise: (schema) => schema,
    })
    .when('$noSpaces', {
      is: () => context?.noSpaces === true,
      then: (schema) =>
        schema.test(
          noSpacesMsg,
          { spaces: noSpacesMsg },
          (value) => !/\s+/.test(value) && value.length > 0,
        ),
      otherwise: (schema) => schema,
    });
};

export const validate = (
  password,
  options = { list: true, abortEarly: false, context: defaultPasswordOptions },
) => {
  return getPasswordSchema(options?.context)
    .validate(password, options)
    .catch(({ errors }) => {
      const validationErrors = errors.reduce((acc, error) => {
        const [key, value] = Object.entries(error)[0];
        acc[key] = value;
        return acc;
      }, {});
      return Promise.resolve({ errors: validationErrors });
    });
};
